import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaFilter, FaTimes, FaProjectDiagram, FaChevronDown, FaCheck, FaClock, FaBell, FaUser, FaCalendarAlt, FaAlignLeft, FaArrowRight, FaTrash, FaExclamationTriangle, FaSortAmountDown, FaConciergeBell, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCopy } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import PageHero from '../components/PageHero/PageHero';
import useConfirm from '../hooks/useConfirm';
import { API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import { notify } from '../utils/notifications';
import './Projects.scss';
import { useDispatch, useSelector } from 'react-redux';
import { setProjects as setProjectsAction } from '../redux/slices/projectsSlice';
import { formatDateDisplay } from '../utils/date';
import { getServiceShortLocationText } from '../utils/location';
import { useLanguage } from '../context/LanguageContext';


const Projects = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('urgency');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isProjectReminderEnabled, setIsProjectReminderEnabled] = useState(false);
  // Add-project modal reminder toggle
  const [addProjectReminderEnabled, setAddProjectReminderEnabled] = useState(false);
  const { register, handleSubmit, reset, setValue: setAddValue, watch, formState: { errors } } = useForm();
  const { register: registerEdit, handleSubmit: handleSubmitEdit, setValue: setEditValue, watch: watchEdit, formState: { errors: errorsEdit } } = useForm();

  const location = useLocation();
  const navigate = useNavigate();
  const pendingOpenRef = useRef(null);

  // Projects are loaded from backend only (no local/temporary storage)
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  // Helper to open a project by id (matches _id or id)
  const openProjectById = (idToOpen) => {
    if (!idToOpen) return false;
    const target = String(idToOpen);
    const proj = projects.find(p => String(p._id || p.id) === target || String(p.id || p._id) === target);
    if (proj) {
      handleViewDetails(proj);
      return true;
    }
    return false;
  };

  // Listen for notification clicks while already on /projects
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/projects') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const idToOpen = state.openViewId;
          if (!openProjectById(idToOpen)) {
            pendingOpenRef.current = idToOpen;
          }
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [projects]);

  // Open add modal when navigated from dashboard with state.openAdd === true
  // Also handles ?id= query param for push clicks when app was closed (SW opens /projects?id=...)
  useEffect(() => {
    if (location?.state?.openAdd) {
      setAddProjectReminderEnabled(notifSettings?.projectRemindersEnabled !== false);
      setShowAddModal(true);
      try {
        window.history.replaceState({}, document.title);
      } catch (e) {}
    }
    const getDeepLinkId = () => {
      if (location?.state?.openViewId) return String(location.state.openViewId);
      try {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('id');
        if (qId) return String(qId);
      } catch (e) {}
      return null;
    };
    const idToOpen = getDeepLinkId();
    if (idToOpen) {
      if (!openProjectById(idToOpen)) {
        pendingOpenRef.current = idToOpen;
      }
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('id')) {
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          window.history.replaceState({}, document.title);
        }
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle push deep-link navigation when app is already open (SW calls client.navigate)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId && !pendingOpenRef.current) {
        if (!openProjectById(String(qId))) {
          pendingOpenRef.current = String(qId);
        } else {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // If pending open was set before projects loaded, retry when projects arrive
  useEffect(() => {
    if (pendingOpenRef.current) {
      const idToOpen = pendingOpenRef.current;
      if (openProjectById(idToOpen)) {
        pendingOpenRef.current = null;
        try { window.history.replaceState({}, document.title); } catch (e) {}
      }
    }
  }, [projects]);

  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { notifications: notifSettings } = useSelector((state) => state.settings);
  // (removed per-record calendar sync toggles)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [showAddStatusMenu, setShowAddStatusMenu] = useState(false);
  const [showEditStatusMenu, setShowEditStatusMenu] = useState(false);
  const [eventQuery, setEventQuery] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [highlightedEventIndex, setHighlightedEventIndex] = useState(-1);
  const eventBlurTimeoutRef = useRef(null);
  const eventInputRef = useRef(null);

  const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

  const formatStatusLabel = (status) => String(status || '').trim() || 'No status';

  const getInitials = (text) => {
    const clean = String(text || '').trim().replace(/\s+/g, ' ');
    if (!clean) return '•';
    const words = clean.split(' ');
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const getDeadlineMeta = (deadline, deadlineTime) => {
    if (!deadline) return { tone: 'none', label: '' };
    try {
      const end = new Date(deadline);
      if (Number.isNaN(end.getTime())) return { tone: 'none', label: '' };
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const diffDays = Math.round((end - start) / 86400000);
      if (diffDays < 0) {
        const d = Math.abs(diffDays);
        return { tone: 'overdue', label: d === 1 ? 'Overdue by 1 day' : `Overdue by ${d} days` };
      }
      if (diffDays === 0) return { tone: 'today', label: deadlineTime ? `Due today · ${deadlineTime}` : 'Due today' };
      if (diffDays === 1) return { tone: 'soon', label: 'Due tomorrow' };
      if (diffDays <= 7) return { tone: 'soon', label: `Due in ${diffDays} days` };
      return { tone: 'upcoming', label: `Due in ${diffDays} days` };
    } catch (e) {
      return { tone: 'none', label: '' };
    }
  };

  const getStatusClassName = (status) => {
    const normalized = normalizeStatus(status);
    if (!normalized) return 'status-custom';
    return ['planning', 'in_progress', 'editing', 'review', 'completed', 'delivered'].includes(normalized)
      ? `status-${normalized}`
      : 'status-custom';
  };

  const isDoneStatus = (status) => ['completed', 'delivered'].includes(normalizeStatus(status));

  // Timestamp used for due-date sorting (date + optional time). No deadline => +Infinity.
  const getDeadlineTimestamp = (project) => {
    if (!project?.deadline) return Number.POSITIVE_INFINITY;
    try {
      const d = new Date(project.deadline);
      if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
      const time = String(project.deadlineTime || '').trim();
      const m = time.match(/^(\d{1,2}):(\d{2})/);
      if (m) {
        d.setHours(Math.min(23, parseInt(m[1], 10) || 0), Math.min(59, parseInt(m[2], 10) || 0), 0, 0);
      } else {
        d.setHours(23, 59, 59, 999);
      }
      return d.getTime();
    } catch (e) {
      return Number.POSITIVE_INFINITY;
    }
  };

  // Urgency rank: overdue (0) -> today (1) -> soon (2) -> upcoming (3) -> no date (4) -> done (5)
  const getUrgencyRank = (project) => {
    if (isDoneStatus(project?.status)) return 5;
    const tone = getDeadlineMeta(project?.deadline, project?.deadlineTime).tone;
    if (tone === 'overdue') return 0;
    if (tone === 'today') return 1;
    if (tone === 'soon') return 2;
    if (tone === 'upcoming') return 3;
    return 4;
  };

  const getStatusSuggestions = (query = '') => {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    const baseList = Array.isArray(projectStatuses) ? projectStatuses : [];

    if (!normalizedQuery) {
      return baseList;
    }

    return baseList.filter((status) => status.toLowerCase().includes(normalizedQuery));
  };

  const getEventIdentifier = (event) => String(event?.id || event?.quotationId || event?._id || '');

  const getEventProjectName = (event) => {
    const eventId = getEventIdentifier(event);
    const firstServiceDate = Array.isArray(event?.services)
      ? event.services.find((service) => service?.date)?.date
      : '';
    const dateStr = formatDateDisplay(firstServiceDate || event?.date);
    if (eventId && dateStr) return `${eventId} - ${dateStr}`;
    if (eventId) return eventId;
    if (dateStr) return dateStr;
    return '';
  };

  // Split an event-generated project name ("EVNT-... - 12 Sep 2026") into
  // id + date rows. Custom names without a date-like second half stay whole.
  const splitProjectName = (name) => {
    const str = String(name || '');
    const sep = ' - ';
    const idx = str.lastIndexOf(sep);
    if (idx === -1) return { head: str, tail: '' };
    const head = str.slice(0, idx).trim();
    const tail = str.slice(idx + sep.length).trim();
    if (!head || !tail || Number.isNaN(new Date(tail).getTime())) return { head: str, tail: '' };
    return { head, tail };
  };

  const getEventLabel = (event) => {
    const eventId = getEventIdentifier(event);
    const clientName = String(event?.clientName || event?.name || '').trim();
    const firstServiceDate = Array.isArray(event?.services)
      ? event.services.find((service) => service?.date)?.date
      : '';
    const parts = [];

    if (clientName) parts.push(clientName);
    if (eventId) parts.push(eventId);
    if (firstServiceDate) parts.push(formatDateDisplay(firstServiceDate));

    return parts.join(' - ') || 'Unnamed event';
  };

  // Linked event + its services for a project (projects store eventId)
  const getLinkedEvent = (project) => {
    const key = String(project?.eventId || '').trim();
    if (!key) return null;
    return (events || []).find((ev) => getEventIdentifier(ev) === key) || null;
  };

  const getLinkedServices = (project) => {
    const linked = getLinkedEvent(project);
    const list = Array.isArray(linked?.services) ? linked.services : [];
    return [...list].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0));
  };

  const loadEvents = async () => {
    try {
      const token = session.getToken();
      if (!token) {
        setEvents([]);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        setEvents([]);
        return;
      }

      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load events for projects:', e);
      setEvents([]);
    }
  };

  const loadProjectStatuses = async () => {
    try {
      const token = session.getToken();
      if (!token) {
        setProjectStatuses([]);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/projects/statuses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        setProjectStatuses([]);
        return;
      }

      const data = await res.json();
      setProjectStatuses(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load project statuses:', e);
      setProjectStatuses([]);
    }
  };

  // Reusable function to refresh projects from server
  const refreshProjectsFromServer = async () => {
    try {
      const token = session.getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setProjects(data);
        dispatch(setProjectsAction(data));
        await loadProjectStatuses();
      }
    } catch (e) {
      console.error('Failed to refresh projects:', e);
    }
  };

  // Publish initial mock projects into Redux so other pages (Dashboard) can read live stats
  useEffect(() => {
    dispatch(setProjectsAction(projects));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load projects from server when authenticated
  useEffect(() => {
    const load = async () => {
      try {
        const token = session.getToken();
        if (!token) {
          setProjects([]);
          dispatch(setProjectsAction([]));
          return;
        }
        const res = await fetch(`${API_BASE_URL}/projects`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          setProjects([]);
          dispatch(setProjectsAction([]));
          return;
        }
        const data = await res.json();
        if (Array.isArray(data)) {
          setProjects(data);
          dispatch(setProjectsAction(data));
          return;
        }
      } catch (e) {
        // ignore
        setProjects([]);
        dispatch(setProjectsAction([]));
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents();
    loadProjectStatuses();
  }, []);

  useEffect(() => {
    const handleEventsChanged = () => {
      loadEvents();
    };

    window.addEventListener('photoflow:events-changed', handleEventsChanged);
    return () => window.removeEventListener('photoflow:events-changed', handleEventsChanged);
  }, []);

  useEffect(() => {
    if (showAddModal) {
      loadEvents();
      loadProjectStatuses();
    }
  }, [showAddModal]);

  const statusOptions = getStatusSuggestions('');

  // Filter, search + sort logic (default: overdue & due first)
  const filteredProjects = projects.filter((project) => {
    const q = (searchTerm || '').toString().trim().toLowerCase();
    const name = (project?.name || '').toString().toLowerCase();
    const client = (project?.client || '').toString().toLowerCase();
    const matchesSearch = q === '' || name.includes(q) || client.includes(q);
    const matchesFilter = filterStatus === 'all' || normalizeStatus(project?.status) === normalizeStatus(filterStatus);
    return matchesSearch && matchesFilter;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (sortOrder === 'due_asc') return getDeadlineTimestamp(a) - getDeadlineTimestamp(b);
    if (sortOrder === 'due_desc') return getDeadlineTimestamp(b) - getDeadlineTimestamp(a);
    if (sortOrder === 'name_asc') return String(a?.name || '').localeCompare(String(b?.name || ''));
    // urgency: overdue -> due today -> due soon -> upcoming -> no date -> completed/delivered
    const rankDiff = getUrgencyRank(a) - getUrgencyRank(b);
    if (rankDiff !== 0) return rankDiff;
    return getDeadlineTimestamp(a) - getDeadlineTimestamp(b);
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = sortedProjects.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, sortOrder]);

  // Handle add new project (use backend as source of truth)
  const handleAddProject = async (data) => {
    const payload = {
      name: data.projectName,
      client: data.clientName,
      status: String(data.status || '').trim(),
      photos: 0,
      deadline: data.deadline,
      deadlineTime: data.deadlineTime || '',
      description: data.description || '',
      eventId: data.eventId || '',
      reminderEnabled: addProjectReminderEnabled,
    };
    setShowAddModal(false);
    setSelectedEventId('');
    setAddProjectReminderEnabled(notifSettings?.projectRemindersEnabled !== false);
    reset();

    try {
      const token = session.getToken();
      if (!token) {
        notify.error('You must be signed in to create a project.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        // Read the response body before any awaited calls consume async context
        const created = await res.json().catch(() => payload);

        // Refresh from server to ensure consistency
        await refreshProjectsFromServer();
        try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}

      } else {
        notify.error('Failed to create project.');
      }
    } catch (e) {
      console.error('Create error:', e);
      notify.error('Failed to create project.');
    }
  };

  // Copy client contact (phone / email) with clipboard fallback
  const handleCopyContact = async (text, label) => {
    const value = String(text || '').trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      notify.success(`${label || 'Contact'} copied`);
    } catch (e) {
      notify.error('Copy failed. Long-press to copy manually.');
    }
  };

  // Handle view details
  const handleViewDetails = (project) => {    setSelectedProject(project);
    setIsEditMode(false);
    setShowDetailsModal(true);
    // Pre-fill form values for editing
    setEditValue('editProjectName', project.name);
    setEditValue('editClientName', project.client);
    setEditValue('editStatus', project.status);
    setEditValue('editDeadline', project.deadline);
    setEditValue('editDeadlineTime', project.deadlineTime || '');
    setEditValue('editDescription', project.description);
  };

  useEffect(() => {
    if (!showDetailsModal) {
      setIsProjectReminderEnabled(false);
      return;
    }

    setIsProjectReminderEnabled(Boolean(selectedProject?.reminderEnabled || selectedProject?.alarmEnabled));
  }, [showDetailsModal, selectedProject?.id, selectedProject?._id]);

  // Immediately persist the reminder toggle to the backend on single click
  const handleToggleProjectReminder = async () => {
    const newValue = !isProjectReminderEnabled;
    setIsProjectReminderEnabled(newValue); // optimistic update
    const projectId = selectedProject?._id || selectedProject?.id;
    try {
      const token = session.getToken();
      if (token && projectId) {
        const res = await fetch(`${API_BASE_URL}/projects/${encodeURIComponent(projectId)}/reminder`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ reminderEnabled: newValue }),
        });
        if (res.ok) {
          setSelectedProject((prev) => ({ ...prev, reminderEnabled: newValue }));
          setProjects((prev) =>
            prev.map((p) =>
              (p._id || p.id) === projectId ? { ...p, reminderEnabled: newValue } : p,
            ),
          );
        } else {
          // Revert on failure
          setIsProjectReminderEnabled(!newValue);
          notify.error('Failed to update reminder. Please try again.');
        }
      }
    } catch (e) {
      setIsProjectReminderEnabled(!newValue);
      notify.error('Failed to update reminder. Please try again.');
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId) => {
    const ok = await confirm('Are you sure you want to delete this project?');
    if (!ok) return;
    
    const deleteId = projectId || selectedProject?._id || selectedProject?.id;
    setShowDetailsModal(false);
    
    try {
      const token = session.getToken();
      if (!token) {
        notify.error('You must be signed in to delete a project.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/projects/${encodeURIComponent(deleteId)}`, { 
        method: 'DELETE', 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) {
        // Refresh from server to ensure consistency
        await refreshProjectsFromServer();
      } else {
        notify.error('Failed to delete project. Please try again.');
      }
    } catch (e) { 
      console.error('Delete error:', e);
      notify.error('Failed to delete project. Please check your connection.');
    }
  };

  // Handle edit project
  const handleEditProject = async (data) => {
    const updatedProject = {
      ...selectedProject,
      name: data.editProjectName,
      client: data.editClientName,
      status: String(data.editStatus || '').trim(),
      deadline: data.editDeadline,
      deadlineTime: data.editDeadlineTime || '',
      description: data.editDescription || '',
      reminderEnabled: isProjectReminderEnabled,
    };
    
    const selectedId = selectedProject?._id || selectedProject?.id;

    try {
      const token = session.getToken();
      if (!token) {
        notify.error('You must be signed in to update a project.');
        return;
      }
      // Update on server first
      const projectId = selectedProject._id || selectedProject.id;
      const res = await fetch(`${API_BASE_URL}/projects/${encodeURIComponent(projectId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updatedProject),
      });
      if (res.ok) {
        // Refresh from server to ensure consistency
        await refreshProjectsFromServer();
        setIsEditMode(false);
        setShowDetailsModal(false);
        notify.success('Project updated successfully');
      } else {
        notify.error('Failed to update project. Please try again.');
        setIsEditMode(true); // Re-enable edit mode if failed
      }
    } catch (e) { 
      console.error('Update error:', e);
      notify.error('Failed to update project. Please check your connection.');
      setIsEditMode(true); // Re-enable edit mode if failed
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form to original values
    setEditValue('editProjectName', selectedProject.name);
    setEditValue('editClientName', selectedProject.client);
    setEditValue('editStatus', selectedProject.status);
    setEditValue('editDeadline', selectedProject.deadline);
    setEditValue('editDeadlineTime', selectedProject.deadlineTime || '');
    setEditValue('editDescription', selectedProject.description);
  };

  const handleAddEventSelect = (eventId) => {
    setSelectedEventId(eventId);
    setAddValue('eventId', eventId);

    if (!eventId) {
      return;
    }

    const selectedEvent = events.find((event) => getEventIdentifier(event) === String(eventId));
    if (!selectedEvent) {
      return;
    }

    setAddValue('projectName', getEventProjectName(selectedEvent));
    setAddValue('clientName', String(selectedEvent.clientName || selectedEvent.name || ''));
  };

  const renderStatusCombobox = ({ field, value, onSelect, showMenu, setShowMenu, error, placeholder, labelId }) => {
    const suggestions = getStatusSuggestions(value);
    const exactMatch = suggestions.some((status) => normalizeStatus(status) === normalizeStatus(value));

    return (
      <div className="status-combobox">
        <div className={`status-combobox__control ${showMenu ? 'is-open' : ''}`}>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            placeholder={placeholder}
            {...field}
            value={value}
            onFocus={() => setShowMenu(true)}
            onChange={(e) => {
              field.onChange(e);
              setShowMenu(true);
            }}
            onBlur={(e) => {
              field.onBlur(e);
              window.setTimeout(() => setShowMenu(false), 120);
            }}
          />
          {value ? (
            <button
              type="button"
              className="status-combobox__clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect('');
                setShowMenu(true);
              }}
              aria-label="Clear status"
            >
              <FaTimes />
            </button>
          ) : (
            <button
              type="button"
              className="status-combobox__chevron"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowMenu((prev) => !prev)}
              aria-label="Toggle status options"
            >
              <FaChevronDown />
            </button>
          )}
        </div>

        {showMenu && (
          <div className="status-combobox__menu" role="listbox" aria-label="Project status options">
            {suggestions.length > 0 ? (
              suggestions.map((status) => {
                const isSelected = normalizeStatus(status) === normalizeStatus(value);
                return (
                  <button
                    key={status}
                    type="button"
                    className={`status-combobox__option ${isSelected ? 'is-selected' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(status)}
                  >
                    <span className="status-combobox__option-label">{formatStatusLabel(status)}</span>
                    {isSelected && <FaCheck />}
                  </button>
                );
              })
            ) : (
              <div className="status-combobox__empty">No saved statuses yet. Type any status you want.</div>
            )}

            {value && !exactMatch && (
              <button
                type="button"
                className="status-combobox__option status-combobox__option--custom"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(value)}
              >
                <span className="status-combobox__option-label">Use “{value}”</span>
              </button>
            )}
          </div>
        )}

        {error && <span className="error">{error.message}</span>}
      </div>
    );
  };

  const handleAddStatusSelect = (status) => {
    setAddValue('status', status, { shouldDirty: true, shouldValidate: true });
    setShowAddStatusMenu(false);
  };

  const handleEditStatusSelect = (status) => {
    setEditValue('editStatus', status, { shouldDirty: true, shouldValidate: true });
    setShowEditStatusMenu(false);
  };

  return (
    <div className="projects">
      <PageHero
        icon={<FaProjectDiagram />}
        title={t('pages.projects')}
        subtitle={t('pages.projectsSubtitle')}
        actions={
          <button className="page-hero__btn" onClick={() => { setAddProjectReminderEnabled(notifSettings?.projectRemindersEnabled !== false); setShowAddModal(true); }}>
            <FaPlus /> {t('project.newProject')}
          </button>
        }
      />

      <div className="projects__filters">
        <div className="search-bar">
          <FaSearch />
          <input
            type="text"
            placeholder={t('project.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm('')}>
              <FaTimes />
            </button>
          )}
        </div>
        <div className="filter-group">
          <FaFilter />
          <CustomSelect
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: 'all', label: t('common.allStatus') },
              ...statusOptions.map((s) => ({ value: s, label: formatStatusLabel(s) }))
            ]}
          />
        </div>
        <div className="filter-group filter-group--sort">
          <FaSortAmountDown />
          <CustomSelect
            value={sortOrder}
            onChange={setSortOrder}
            options={[
              { value: 'urgency', label: 'Overdue & due first' },
              { value: 'due_asc', label: 'Due: soonest first' },
              { value: 'due_desc', label: 'Due: latest first' },
              { value: 'name_asc', label: 'Name: A to Z' },
            ]}
          />
        </div>
      </div>

      {/* Results count */}
      <div className="projects__results">
        {t('common.showing')} {paginatedProjects.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, sortedProjects.length)} {t('common.of')} {sortedProjects.length} {t('project.projectsLabel')}
      </div>

      {/* Projects Grid */}
      <div className="projects__grid">
        {paginatedProjects.length > 0 ? (
          paginatedProjects.map((project, index) => {
            const { head, tail } = splitProjectName(project.name);
            const statusClass = getStatusClassName(project.status);
            const due = getDeadlineMeta(project.deadline, project.deadlineTime);
            const cardId = project._id || project.id;
            return (
            <article
              key={cardId}
              className={`project-card project-card--${statusClass}`}
              style={{ '--card-index': index % itemsPerPage }}
              onClick={() => handleViewDetails(project)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleViewDetails(project);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`View ${head || project.name} details`}
            >
              <span className="project-card__accent" aria-hidden="true" />
              <div className="project-card__top">
                <span className="project-card__avatar" aria-hidden="true">{getInitials(project.client || head)}</span>
                <div className="project-card__title-wrap">
                  <h3 className="project-card__name" title={project.name}>
                    <span className="project-card__name-id">{head}</span>
                    {tail && (
                      <span className="project-card__name-date"><FaCalendarAlt /> {tail}</span>
                    )}
                  </h3>
                </div>
                <span className={`project-card__status ${statusClass}`}>
                  {formatStatusLabel(project.status)}
                </span>
              </div>

              {due.tone !== 'none' && (
                <div className={`project-card__due project-card__due--${due.tone}`}>
                  {due.tone === 'overdue' || due.tone === 'today' ? <FaExclamationTriangle /> : <FaClock />}
                  <span>{due.label}</span>
                </div>
              )}

              <div className="project-card__details">
                <div className="project-card__meta">
                  <span className="project-card__meta-icon project-card__meta-icon--client"><FaUser /></span>
                  <span className="project-card__meta-body">
                    <span className="project-card__meta-label">{t('common.client')}</span>
                    <span className="project-card__meta-value">{project.client || '—'}</span>
                  </span>
                  {(project.reminderEnabled || project.alarmEnabled) && (
                    <span className="project-card__bell" title="Reminder on"><FaBell /></span>
                  )}
                </div>
                <div className="project-card__meta project-card__meta--deadline">
                  <span className="project-card__meta-icon project-card__meta-icon--deadline"><FaCalendarAlt /></span>
                  <span className="project-card__meta-body">
                    <span className="project-card__meta-label">{t('project.labelDeadlineView')}</span>
                    <span className="project-card__datetime">
                      <span className="project-card__dt">
                        <span className="project-card__dt-label">Date</span>
                        <span className="project-card__dt-value project-card__dt-value--date">
                          {formatDateDisplay(project.deadline) || '—'}
                        </span>
                      </span>
                      <span className="project-card__dt-divider" aria-hidden="true" />
                      <span className="project-card__dt">
                        <span className="project-card__dt-label">Time</span>
                        <span className={`project-card__dt-value project-card__dt-value--time ${!project.deadlineTime ? 'is-empty' : ''}`}>
                          <FaClock /> {project.deadlineTime || '—'}
                        </span>
                      </span>
                    </span>
                  </span>
                </div>
                {(() => {
                  const linked = getLinkedEvent(project);
                  if (!project?.eventId || !linked) return null;
                  const svcs = getLinkedServices(project);
                  if (!svcs.length) return null;
                  const visible = svcs.slice(0, 2);
                  const extra = svcs.length - visible.length;
                  return (
                    <div className="project-card__services">
                      <div className="project-card__services-head">
                        <FaConciergeBell />
                        <span>Event services · {svcs.length}</span>
                      </div>
                      <div className="project-card__services-chips">
                        {visible.map((svc, i) => (
                          <span key={svc?.id || `${svc?.name || 'service'}-${i}`} className="project-card__service-chip" title={svc?.name || 'Service'}>
                            <span className="project-card__service-name">{svc?.name || 'Unnamed service'}</span>
                            {svc?.date && (
                              <span className="project-card__service-date">{formatDateDisplay(svc.date)}{svc?.time ? ` · ${svc.time}` : ''}</span>
                            )}
                          </span>
                        ))}
                        {extra > 0 && (
                          <span className="project-card__service-more">+{extra} more</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <p className={`project-card__description ${!project.description ? 'project-card__description--empty' : ''}`}>
                  <FaAlignLeft className="project-card__description-icon" />
                  <span>{project.description || t('project.noDescHint')}</span>
                </p>
              </div>
              <div className="project-card__actions">
                <button
                  type="button"
                  className="project-card__delete"
                  onClick={(e) => { e.stopPropagation(); handleDeleteProject(cardId); }}
                  aria-label={`Delete ${head || 'project'}`}
                  title={t('project.deleteProject')}
                >
                  <FaTrash /> <span>{t('project.deleteProject')}</span>
                </button>
                <button
                  type="button"
                  className="project-card__view"
                  onClick={(e) => { e.stopPropagation(); handleViewDetails(project); }}
                >
                  <span>{t('project.viewDetails')}</span> <FaArrowRight />
                </button>
              </div>
            </article>
            );
          })
        ) : (
          <div className="projects__empty">
            <p>{t('project.emptyTitle')}</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            {t('common.prev')}
          </button>
          <span>{t('project.page')} {currentPage} {t('common.of')} {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Add Project Modal */}
          <Modal
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false);
              setSelectedEventId('');
              setEventQuery('');
              setShowEventDropdown(false);
              setHighlightedEventIndex(-1);
              setAddProjectReminderEnabled(false);
              reset();
            }}
            title={t('project.addTitle')}
            headerActions={(
              <>
                <button
                  type="button"
                  className={`modal__toggle-btn ${addProjectReminderEnabled ? 'modal__toggle-btn--active' : 'modal__toggle-btn--inactive'}`}
                  onClick={() => setAddProjectReminderEnabled((prev) => !prev)}
                  aria-pressed={addProjectReminderEnabled}
                  aria-label={`Project reminder ${addProjectReminderEnabled ? 'on' : 'off'}`}
                  title={`Project reminder ${addProjectReminderEnabled ? 'on' : 'off'}`}
                >
                  {addProjectReminderEnabled ? <FaBell className="modal__toggle-icon" /> : <FaClock className="modal__toggle-icon" />}
                  <span className="modal__toggle-state">{addProjectReminderEnabled ? 'On' : 'Off'}</span>
                </button>
              </>
            )}
            size="medium"
          >
        <form onSubmit={handleSubmit(handleAddProject)} className="project-form">
          <div className="form-group">
            <label htmlFor="eventId">{t('project.selectEvent')}</label>
            <div style={{ position: 'relative' }}>
              {(() => {
                const all = events || [];
                const filtered = all.filter(ev => {
                  const label = getEventLabel(ev);
                  return label.toLowerCase().includes((eventQuery || '').toLowerCase());
                });

                const selectedLabel = (() => {
                  if (eventQuery) return eventQuery;
                  if (selectedEventId) {
                    const found = all.find(x => getEventIdentifier(x) === String(selectedEventId));
                    if (found) return getEventLabel(found);
                  }
                  return '';
                })();

                return (
                  <>
                    <input
                      ref={eventInputRef}
                      className="event__quotation-input"
                      type="text"
                      placeholder="Search event by ID or client name"
                      value={eventQuery || selectedLabel}
                      onChange={(e) => { setEventQuery(e.target.value); setShowEventDropdown(true); setHighlightedEventIndex(-1); }}
                      onFocus={() => { setShowEventDropdown(true); setHighlightedEventIndex(-1); }}
                      onKeyDown={(e) => {
                        const max = filtered.length - 1;
                        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedEventIndex(i => Math.min(max, Math.max(0, i + 1))); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedEventIndex(i => Math.max(0, i - 1)); }
                        else if (e.key === 'Enter') {
                          e.preventDefault();
                          if (highlightedEventIndex >= 0 && filtered[highlightedEventIndex]) {
                            const sel = filtered[highlightedEventIndex];
                            const selId = getEventIdentifier(sel);
                            handleAddEventSelect(selId);
                            const label = getEventLabel(sel);
                            setEventQuery(label);
                            setShowEventDropdown(false);
                            setHighlightedEventIndex(-1);
                          }
                        } else if (e.key === 'Escape') {
                          setShowEventDropdown(false);
                        }
                      }}
                      onBlur={() => { eventBlurTimeoutRef.current = setTimeout(() => setShowEventDropdown(false), 120); }}
                      onMouseDown={() => { if (eventBlurTimeoutRef.current) { clearTimeout(eventBlurTimeoutRef.current); eventBlurTimeoutRef.current = null; } }}
                    />

                    {showEventDropdown && filtered.length > 0 && (
                      <ul className="event__quotation-dropdown" style={{ position: 'absolute', zIndex: 40, top: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 220, overflow: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 0, margin: 0, listStyle: 'none' }}>
                        {filtered.map((ev, idx) => {
                          const label = getEventLabel(ev);
                          const isHighlighted = idx === highlightedEventIndex;
                          return (
                            <li
                              key={(getEventIdentifier(ev) || '') + '-' + idx}
                              onMouseDown={(evd) => { evd.preventDefault(); const selId = getEventIdentifier(ev); handleAddEventSelect(selId); setEventQuery(label); setShowEventDropdown(false); setHighlightedEventIndex(-1); }}
                              onMouseEnter={() => setHighlightedEventIndex(idx)}
                              style={{ padding: '8px 10px', background: isHighlighted ? '#f3f4f6' : '#fff', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                            >
                              <div style={{ fontSize: 13, color: '#111' }}>{label}</div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {(eventQuery || selectedEventId) && (
                      <button
                        type="button"
                        className="event__quotation-clear"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setEventQuery('');
                          setSelectedEventId('');
                          try { setAddValue('eventId', ''); setAddValue('projectName', ''); setAddValue('clientName', ''); } catch (err) {}
                          setShowEventDropdown(false);
                          setHighlightedEventIndex(-1);
                          try { eventInputRef.current && eventInputRef.current.focus(); } catch (err) {}
                        }}
                        aria-label="Clear event"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
            <small className="field-hint">
              Selecting an event prefills project and client names. You can still edit both fields.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="projectName">Project Name *</label>
            <input
              id="projectName"
              type="text"
              placeholder="e.g., Wedding - Sarah & Mike"
              {...register('projectName', { required: 'Project name is required' })}
            />
            {errors.projectName && <span className="error">{errors.projectName.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="clientName">Client Name *</label>
            <input
              id="clientName"
              type="text"
              placeholder="e.g., Sarah Johnson"
              {...register('clientName', { required: 'Client name is required' })}
            />
            {errors.clientName && <span className="error">{errors.clientName.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="status">Project Status *</label>
            {renderStatusCombobox({
              field: register('status', { required: 'Status is required' }),
              value: watch('status') || '',
              onSelect: handleAddStatusSelect,
              showMenu: showAddStatusMenu,
              setShowMenu: setShowAddStatusMenu,
              error: errors.status,
              placeholder: 'Type or choose a status',
              labelId: 'status',
            })}
          </div>

          <div className="project-form__date-time-grid">
            <div className="form-group">
              <label htmlFor="deadline">Deadline *</label>
              <input
                id="deadline"
                type="date"
                {...register('deadline', { required: 'Deadline is required' })}
              />
              {errors.deadline && <span className="error">{errors.deadline.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="deadlineTime">{t('project.labelDeadlineTime')} <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.85em' }}></span></label>
              <input
                id="deadlineTime"
                type="time"
                {...register('deadlineTime')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description"><strong>Description:</strong></label>
            <textarea
              id="description"
              rows="4"
              placeholder="Project description..."
              {...register('description')}
            />
          </div>

          <div className="form-actions">
            <div style={{ flex: 1 }} />
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => {
                  setShowAddModal(false);
                  setSelectedEventId('');
                  setEventQuery('');
                  setShowEventDropdown(false);
                  setHighlightedEventIndex(-1);
                  reset();
                }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Create Project
            </button>
          </div>
        </form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedProject(null);
          setIsEditMode(false);
        }}
        title={isEditMode ? "Edit Project" : "Project Details"}
        headerActions={(
          <>
            {/* Reminder bell toggle */}
            <button
              type="button"
              className={`modal__toggle-btn ${isProjectReminderEnabled ? 'modal__toggle-btn--active' : 'modal__toggle-btn--inactive'}`}
              onClick={handleToggleProjectReminder}
              aria-pressed={isProjectReminderEnabled}
              aria-label={`Project reminder ${isProjectReminderEnabled ? 'on' : 'off'}`}
              title={`Project reminder ${isProjectReminderEnabled ? 'on' : 'off'}`}
            >
              {isProjectReminderEnabled ? <FaBell className="modal__toggle-icon" /> : <FaClock className="modal__toggle-icon" />}
              <span className="modal__toggle-state">{isProjectReminderEnabled ? 'On' : 'Off'}</span>
            </button>
            {/* Google Calendar per-project toggle removed */}
          </>
        )}
        size="large"
      >
        {selectedProject && !isEditMode && (
          <div className="project-details">
            <div className={`project-details__hero project-details__hero--${getStatusClassName(selectedProject.status)}`}>
              <span className="project-details__hero-glow" aria-hidden="true" />
              <div className="project-details__title-wrap">
                <span className="project-details__avatar" aria-hidden="true">{getInitials(selectedProject.client || selectedProject.name)}</span>
                <div className="project-details__title-text">
                  {(() => {
                    const { head, tail } = splitProjectName(selectedProject.name);
                    return (
                      <>
                        <h2 title={selectedProject.name}>{head}</h2>
                        {tail ? (
                          <p className="project-details__date"><FaCalendarAlt /> {tail}</p>
                        ) : (
                          <p className="project-details__date project-details__date--muted">
                            <FaUser /> {selectedProject.client || '—'}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <span
                className={`status-badge ${getStatusClassName(selectedProject.status)}`}
              >
                {formatStatusLabel(selectedProject.status)}
              </span>
            </div>

            {(() => {
              const due = getDeadlineMeta(selectedProject.deadline, selectedProject.deadlineTime);
              return due.tone !== 'none' ? (
                <div className={`project-details__due project-details__due--${due.tone}`}>
                  {due.tone === 'overdue' || due.tone === 'today' ? <FaExclamationTriangle /> : <FaClock />}
                  <span>{due.label} · {formatDateDisplay(selectedProject.deadline) || ''}</span>
                </div>
              ) : null;
            })()}

            <div className="project-details__grid">
              <div className="detail-item detail-item--client">
                <span className="detail-item__icon"><FaUser /></span>
                <div className="detail-item__body">
                  <label>{t('project.labelClient')}</label>
                  <p>{selectedProject.client || '—'}</p>
                  {(() => {
                    const linked = getLinkedEvent(selectedProject);
                    const phone = String(linked?.clientPhone || selectedProject?.clientPhone || selectedProject?.phone || '').trim();
                    const email = String(linked?.clientEmail || selectedProject?.clientEmail || selectedProject?.email || '').trim();
                    if (!phone && !email) {
                      return <span className="detail-item__sub is-muted">No contact saved</span>;
                    }
                    return (
                      <span className="detail-item__contact">
                        {phone && (
                          <span className="detail-item__contact-line">
                            <span className="detail-item__contact-row">
                              <FaPhone />
                              <a className="detail-item__contact-text-link" href={`tel:${phone.replace(/\s+/g, '')}`} title={phone}>
                                <span className="detail-item__contact-text">{phone}</span>
                              </a>
                            </span>
                            <button
                              type="button"
                              className="detail-item__copy"
                              onClick={() => handleCopyContact(phone, 'Phone number')}
                              aria-label={`Copy phone ${phone}`}
                              title="Copy phone"
                            >
                              <FaCopy />
                            </button>
                          </span>
                        )}
                        {email && (
                          <span className="detail-item__contact-line">
                            <span className="detail-item__contact-row">
                              <FaEnvelope />
                              <a className="detail-item__contact-text-link" href={`mailto:${email}`} title={email}>
                                <span className="detail-item__contact-text">{email}</span>
                              </a>
                            </span>
                            <button
                              type="button"
                              className="detail-item__copy"
                              onClick={() => handleCopyContact(email, 'Email')}
                              aria-label={`Copy email ${email}`}
                              title="Copy email"
                            >
                              <FaCopy />
                            </button>
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="detail-item detail-item--deadline">
                <span className="detail-item__icon"><FaCalendarAlt /></span>
                <div className="detail-item__body">
                  <label>{t('project.labelDeadlineView')}</label>
                  <div className="detail-item__datetime">
                    <div className="detail-item__dt">
                      <span className="detail-item__dt-label">Date</span>
                      <span className="detail-item__dt-value detail-item__dt-value--date">
                        {formatDateDisplay(selectedProject.deadline) || '—'}
                      </span>
                    </div>
                    <span className="detail-item__dt-divider" aria-hidden="true" />
                    <div className="detail-item__dt">
                      <span className="detail-item__dt-label">Time</span>
                      <span className={`detail-item__dt-value detail-item__dt-value--time ${!selectedProject.deadlineTime ? 'is-empty' : ''}`}>
                        <FaClock /> {selectedProject.deadlineTime || '—'}
                      </span>
                      {!selectedProject.deadlineTime && (
                        <span className="detail-item__dt-hint">09:00 default</span>
                      )}
                    </div>
                  </div>
                  {/* <span className="detail-item__sub">Delivery target</span> */}
                </div>
              </div>
            </div>

            {selectedProject?.eventId && (() => {
              const linked = getLinkedEvent(selectedProject);
              if (!linked) {
                return (
                  <div className="project-details__services project-details__services--empty">
                    <div className="project-details__services-head">
                      <FaConciergeBell />
                      <span>Event services</span>
                    </div>
                    <p>Linked event no longer exists.</p>
                  </div>
                );
              }
              const svcs = getLinkedServices(selectedProject);
              return (
                <div className="project-details__services">
                  <div className="project-details__services-head">
                    <FaConciergeBell />
                    <span>Event services</span>
                    <span className="project-details__services-count">{svcs.length}</span>
                  </div>
                  <div className="project-details__services-event" title={getEventLabel(linked)}>
                    {getEventLabel(linked)}
                  </div>
                  {svcs.length > 0 ? (
                    <ul className="project-details__services-list">
                      {svcs.map((svc, i) => {
                        const shortLoc = getServiceShortLocationText(svc);
                        return (
                        <li key={svc?.id || `${svc?.name || 'service'}-${i}`} className="project-details__service">
                          <span className="project-details__service-icon"><FaConciergeBell /></span>
                          <span className="project-details__service-body">
                            <span className="project-details__service-name">{svc?.name || 'Unnamed service'}</span>
                            <span className="project-details__service-meta">
                              {svc?.date && (
                                <span><FaCalendarAlt /> {formatDateDisplay(svc.date)}</span>
                              )}
                              {svc?.time && (
                                <span><FaClock /> {svc.time}</span>
                              )}
                              {shortLoc && (
                                <span className="project-details__service-loc" title={shortLoc}><FaMapMarkerAlt /> <span className="project-details__service-loc-text">{shortLoc}</span></span>
                              )}
                              {!svc?.date && !svc?.time && !shortLoc && (
                                <span className="is-muted">No schedule set</span>
                              )}
                            </span>
                          </span>
                          {svc?.status && (
                            <span className={`project-details__service-status project-details__service-status--${String(svc.status).toLowerCase().replace(/\s+/g, '_')}`}>
                              {String(svc.status).replace(/_/g, ' ')}
                            </span>
                          )}
                        </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="project-details__services-empty">No services added to this event yet.</p>
                  )}
                </div>
              );
            })()}

            <div className="project-details__description">
              <label><FaAlignLeft /> <strong>{t('project.colDescription')}</strong></label>
              <p className={!selectedProject.description ? 'project-details__description--empty' : ''}>
                {selectedProject.description || 'No description provided'}
              </p>
            </div>

            <div className="project-details__actions">
              <button 
                className="btn-danger"
                onClick={() => handleDeleteProject(selectedProject._id || selectedProject.id)}
              >
                Delete
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => setIsEditMode(true)}
              >
                Edit
              </button>
            </div>
          </div>
        )}

        {selectedProject && isEditMode && (
          <form onSubmit={handleSubmitEdit(handleEditProject)} className="project-form">
            <div className="form-group">
              <label htmlFor="editProjectName">Project Name *</label>
              <input
                id="editProjectName"
                type="text"
                placeholder="e.g., Wedding - Sarah & Mike"
                {...registerEdit('editProjectName', { required: 'Project name is required' })}
              />
              {errorsEdit.editProjectName && <span className="error">{errorsEdit.editProjectName.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="editClientName">Client Name *</label>
              <input
                id="editClientName"
                type="text"
                placeholder="e.g., Sarah Johnson"
                {...registerEdit('editClientName', { required: 'Client name is required' })}
              />
              {errorsEdit.editClientName && <span className="error">{errorsEdit.editClientName.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="editStatus">Project Status *</label>
              {renderStatusCombobox({
                field: registerEdit('editStatus', { required: 'Status is required' }),
                value: watchEdit('editStatus') || '',
                onSelect: handleEditStatusSelect,
                showMenu: showEditStatusMenu,
                setShowMenu: setShowEditStatusMenu,
                error: errorsEdit.editStatus,
                placeholder: 'Type or choose a status',
                labelId: 'editStatus',
              })}
            </div>

            <div className="project-form__date-time-grid">
              <div className="form-group">
                <label htmlFor="editDeadline">Deadline *</label>
                <input
                  id="editDeadline"
                  type="date"
                  {...registerEdit('editDeadline', { required: 'Deadline is required' })}
                />
                {errorsEdit.editDeadline && <span className="error">{errorsEdit.editDeadline.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="editDeadlineTime">Deadline Time <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.85em' }}></span></label>
                <input
                  id="editDeadlineTime"
                  type="time"
                  {...registerEdit('editDeadlineTime')}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="editDescription"><strong>Description:</strong></label>
              <textarea
                id="editDescription"
                rows="4"
                placeholder="Project description..."
                {...registerEdit('editDescription')}
              />
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Save Changes
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Projects;
